import { ShiftActivityCard } from "./ShiftActivityCard";
import { ActivityStatusButtons } from "./ActivityStatus";
import { Activity, Status } from "../../../types";
import { ActivityStatus, Priority } from "../../../api/generated/graphql";
import { Button } from "@frontend/lyng/button";
import { Select } from "@frontend/lyng/forms";
import { useTranslate } from "@tolgee/react";
import Berries from "../../../assets/berries.svg?react";
import Leaves from "../../../assets/leaves.svg?react";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { Paragraph } from "@frontend/lyng/typography";
import classNames from "classnames";

type Props = {
  activities: Activity[];
  visitStatus: Status;
  visitInstanceId: string;
  displayView?: boolean;
  isOtherActivities?: boolean;
  nonDueActivitiesCount: number;
};

const NoDueActivities = ({
  notDueActivitiesCount,
}: {
  notDueActivitiesCount: number;
}) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate("./other-activities");
  };

  return (
    <div className="flex grow flex-col gap-8 px-2 pt-2 items-center">
      <div className="flex flex-row items-center justify-between w-2/3 px-8">
        <Berries className="h-16 w-16" />
        <Leaves className="h-16 w-16" />
      </div>
      <Paragraph>{t("activites.noDueActivities")}</Paragraph>
      <Button
        text={`${notDueActivitiesCount} ${t("activites.otherActivities")}`}
        variant="secondary"
        onClick={handleClick}
      />
    </div>
  );
};

export const ShiftActivities = ({
  activities,
  nonDueActivitiesCount,
  visitStatus,
  visitInstanceId,
  displayView,
  isOtherActivities = false,
}: Props) => {
  const { t } = useTranslate();
  const [selectedActivityType, setSelectedActivityType] = useState<
    string | null
  >(null);

  // Get unique activity types from activities
  const activityTypes = useMemo(() => {
    const uniqueTypes = new Set<NonNullable<Activity["activityType"]>>();

    activities.forEach((activity) => {
      if (activity.activityType) {
        uniqueTypes.add(activity.activityType);
      }
    });

    return Array.from(uniqueTypes);
  }, [activities]);

  // Filter activities based on selected activity type
  const filteredActivities = useMemo(() => {
    if (!selectedActivityType) {
      return activities;
    }
    return activities.filter(
      (activity) => activity.activityType?.id === selectedActivityType,
    );
  }, [activities, selectedActivityType]);

  // Create dropdown options
  const dropdownOptions = useMemo(() => {
    const options = [
      ...activityTypes.map((type) => ({
        id: type.id,
        label: t(`activityType.${type.id}`),
        onClick: () => setSelectedActivityType(type.id),
      })),
    ];
    return options;
  }, [activityTypes, t]);

  return filteredActivities && filteredActivities.length > 0 ? (
    <div className="flex grow flex-col px-2">
      {/* Filter Dropdown */}
      {dropdownOptions.length > 0 && (
        <div className="flex justify-start mb-4">
          <Select
            options={dropdownOptions}
            value={
              dropdownOptions.find((cr) => cr.id === selectedActivityType) ||
              null
            }
            isMulti={false}
            isClearable={true}
            placeholder={t("activities.allActivities")}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.id}
            onChange={(option) =>
              setSelectedActivityType(option ? option.id : null)
            }
            name="activityType"
            menuPlacement="bottom"
            isSearchable={false}
          />
        </div>
      )}
      {filteredActivities.map((activity, idx) => {
        const isLast = idx === filteredActivities.length - 1;
        return (
          <div
            key={`${activity.id}${activity.homeStayId ?? ""}`}
            className={classNames("flex flex-col gap-2", {
              "border-b border-greyscale-700": !isLast,
            })}
          >
            <ShiftActivityCard
              priority={activity.priority ?? Priority.Optional}
              isDue={activity.isDue}
              lastCompletedAt={activity.lastCompletedAt}
              schedule={activity.schedule}
              title={activity.title}
              description={activity.description}
              start={activity.timeOfDayStart ?? undefined}
              subject={activity.subject}
              status={activity.status ?? ActivityStatus.NotStarted}
              completedBy={activity.completedBy}
              comment={activity.comment}
            />
            {visitStatus !== "pending" && !displayView && (
              <div className="pb-4">
                <ActivityStatusButtons
                  shouldShowNotCompletedButton={
                    !(
                      !activity.isDue &&
                      isOtherActivities &&
                      activity.status === ActivityStatus.NotStarted
                    )
                  }
                  status={activity.status ?? ActivityStatus.NotStarted}
                  activityId={activity.id}
                  visitInstanceId={visitInstanceId}
                  visitStatus={visitStatus}
                  homeStayId={activity.homeStayId}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  ) : (
    <div className="flex grow flex-col gap-8 px-2 pt-2">
      <NoDueActivities notDueActivitiesCount={nonDueActivitiesCount} />
    </div>
  );
};
